/**
 * GenerationProgress component - Shows image generation progress.
 *
 * Design: DENiZEN
 * - Soft shadows and rounded corners
 * - Muted palette with mid-green and teal accents
 * - Smooth progress bar
 * - WCAG 2.1 AA compliant
 */

import { useState, useEffect } from 'react'

/**
 * Generation status
 */
export type GenerationStatus = 'queued' | 'generating' | 'validating' | 'complete' | 'failed'

export interface GenerationProgressProps {
  /**
   * Current generation status
   */
  status: GenerationStatus
  /**
   * Estimated generation time in seconds
   */
  estimatedSeconds?: number
  /**
   * Whether this is a refinement iteration
   */
  isRefinement?: boolean
  /**
   * Current iteration number
   */
  iterationNumber?: number
  /**
   * Error message if generation failed
   */
  errorMessage?: string
  /**
   * Callback when user wants to retry
   */
  onRetry?: () => void
}

/**
 * Status messages for each generation phase
 */
const STATUS_MESSAGES: Record<GenerationStatus, string> = {
  queued: 'Preparing your image...',
  generating: 'Creating your vision of the future...',
  validating: 'Ensuring your image is ready to share...',
  complete: 'Your image is ready!',
  failed: 'Something went wrong. Please try again.',
}

export function GenerationProgress({
  status,
  estimatedSeconds = 15,
  isRefinement = false,
  iterationNumber = 1,
  errorMessage,
  onRetry,
}: GenerationProgressProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [progressPercentage, setProgressPercentage] = useState(0)

  // Track elapsed time
  useEffect(() => {
    if (status === 'generating' || status === 'validating') {
      const interval = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1)
      }, 1000)

      return () => clearInterval(interval)
    }
    return undefined
  }, [status])

  // Calculate progress percentage
  useEffect(() => {
    if (status === 'queued') {
      setProgressPercentage(5)
    } else if (status === 'generating') {
      // Progress from 10% to 80% during generation
      const progress = Math.min(10 + (elapsedSeconds / estimatedSeconds) * 70, 80)
      setProgressPercentage(progress)
    } else if (status === 'validating') {
      setProgressPercentage(90)
    } else if (status === 'complete') {
      setProgressPercentage(100)
    }
  }, [status, elapsedSeconds, estimatedSeconds])

  // Reset elapsed time when starting new generation
  useEffect(() => {
    if (status === 'queued') {
      setElapsedSeconds(0)
    }
  }, [status])

  const isActive = status !== 'complete' && status !== 'failed'

  return (
    <div
      className="max-w-md mx-auto animate-slide-up"
      role="status"
      aria-live="polite"
      aria-busy={isActive || undefined}
    >
      {/* Main card - DENiZEN */}
      <div className="denizen-card p-8 text-center relative">
        {/* Status label */}
        <div className={`pebble-label absolute -top-3 -right-3 text-xs ${
          status === 'complete' ? 'bg-mid-green' :
          status === 'failed' ? 'pebble-label-error' : ''
        }`}>
          {status === 'complete' ? 'DONE' :
           status === 'failed' ? 'ERROR' : 'LOADING'}
        </div>

        {/* Icon/Animation */}
        <div className="mb-6 flex justify-center">
          {status === 'failed' ? (
            <div className="w-20 h-20 bg-error border border-dark-green/15 shadow-denizen rounded-2xl flex items-center justify-center">
              <svg
                className="w-10 h-10 text-white"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ) : status === 'complete' ? (
            <div className="w-20 h-20 bg-mid-green border border-dark-green/15 shadow-denizen rounded-2xl flex items-center justify-center animate-stamp">
              <svg
                className="w-10 h-10 text-dark-green"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ) : (
            <div className="w-20 h-20 bg-teal border border-dark-green/15 shadow-denizen rounded-2xl flex items-center justify-center relative">
              {/* Spinning dots */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex gap-1">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              </div>
              {/* Image icon */}
              <svg
                className="w-8 h-8 text-white opacity-50"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-display font-bold text-dark-green tracking-tight mb-2">
          {isRefinement ? (
            <>Refining <span className="highlight-marker">#{iterationNumber}</span></>
          ) : (
            <>Generating <span className="highlight-marker">Vision</span></>
          )}
        </h2>

        {/* Status message */}
        <p className="text-sm font-body text-muted mb-6">
          {errorMessage || STATUS_MESSAGES[status]}
        </p>

        {/* Progress bar - DENiZEN style */}
        {isActive && (
          <div className="mb-4">
            <div
              className="w-full h-4 bg-stone border border-dark-green/15 overflow-hidden rounded-full"
              role="progressbar"
              aria-label="Image generation progress"
              aria-valuenow={Math.round(progressPercentage)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full bg-mid-green transition-all duration-500 ease-out rounded-full progress-bar-fill"
              />
            </div>
            <style>{`
              .progress-bar-fill { width: ${progressPercentage}%; }
            `}</style>
            <div className="flex justify-between mt-2">
              <span className="text-xs font-display font-bold text-dark-green">
                {Math.round(progressPercentage)}%
              </span>
              <span className="text-xs font-body text-muted">
                {status === 'generating' && elapsedSeconds > 0
                  ? `${elapsedSeconds}s / ~${estimatedSeconds}s`
                  : 'Starting...'}
              </span>
            </div>
          </div>
        )}

        {/* Retry button - DENiZEN style */}
        {status === 'failed' && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="btn-denizen-primary mt-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </button>
        )}

        {/* Accessibility: Screen reader announcement */}
        <span className="sr-only">
          {isActive
            ? `Image generation in progress. ${Math.round(progressPercentage)}% complete.`
            : status === 'complete'
            ? 'Image generation complete.'
            : 'Image generation failed.'}
        </span>
      </div>

    </div>
  )
}

export default GenerationProgress
